/**
 * Subscription Success Page
 * Shown after successful subscription checkout
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../contexts/CreditsContext';
import { getApiBase } from '../../config/api';
import './SubscriptionSuccess.css';

export const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshBalance } = useCredits();
  const [countdown, setCountdown] = useState(5);
  const [verifying, setVerifying] = useState(true);
  const [verificationResult, setVerificationResult] = useState<{ planName?: string; credits?: number } | null>(null);

  const sessionId = searchParams.get('session_id');
  const returnUrlParam = searchParams.get('return_url');
  // Get return URL from query param, sessionStorage, or default to dashboard
  const returnUrl = returnUrlParam || sessionStorage.getItem('pre-subscription-url') || '/';
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent double-verification from React StrictMode or re-renders
    if (hasVerified.current) return;
    
    // Verify subscription and get credits
    const verifySubscription = async () => {
      if (!sessionId || !user?.id) {
        setVerifying(false);
        return;
      }
      
      hasVerified.current = true;

      try {
        const response = await fetch(`${getApiBase()}/api/subscriptions/verify/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': user.id,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setVerificationResult({
            planName: data.subscription?.planName,
            credits: data.subscription?.credits,
          });
          console.log('[SubscriptionSuccess] Verified:', data);
        } else {
          console.warn('[SubscriptionSuccess] Verification failed:', await response.text());
        }
      } catch (error) {
        console.error('[SubscriptionSuccess] Error verifying:', error);
      } finally {
        setVerifying(false);
        // Refresh balance after verification attempt
        refreshBalance();
      }
    };

    verifySubscription();
  }, [sessionId, user?.id, refreshBalance]);

  useEffect(() => {
    // Only start countdown after verification is done
    if (verifying) return;

    // Auto-redirect countdown - return to original flow if available
    const redirectTarget = returnUrl || '/';
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(redirectTarget);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [verifying, navigate]);

  return (
    <div className="subscription-success-page">
      <div className="success-container">
        <div className="success-icon">
          <CheckCircleIcon />
        </div>

        <h1>Subscription Activated!</h1>
        
        <p className="success-message">
          {verificationResult?.planName && verificationResult?.credits ? (
            <>
              Welcome to <strong>{verificationResult.planName}</strong>! 
              {' '}{verificationResult.credits.toLocaleString()} credits have been added to your account.
            </>
          ) : (
            <>
              Thank you for subscribing! Your credits have been added to your account
              and will refresh automatically each billing period.
            </>
          )}
        </p>

        <div className="success-details">
          <p>You now have access to:</p>
          <ul>
            <li>Your monthly credit allocation</li>
            <li>All AI models</li>
            <li>Unlimited knod creations</li>
            <li>Priority support</li>
          </ul>
        </div>

        <div className="success-actions">
          {returnUrl && returnUrl !== '/' ? (
            <button className="primary-button" onClick={() => {
              sessionStorage.removeItem('pre-subscription-url');
              navigate(returnUrl);
            }}>
              Return to Your Knod
            </button>
          ) : (
            <button className="primary-button" onClick={() => navigate('/flow')}>
              Create New Knod
            </button>
          )}
          <button className="secondary-button" onClick={() => {
            sessionStorage.removeItem('pre-subscription-url');
            navigate('/');
          }}>
            Go to Dashboard
          </button>
        </div>

        <p className="redirect-notice">
          {verifying ? 'Activating your subscription...' : `Redirecting in ${countdown} seconds...`}
        </p>

        {sessionId && (
          <p className="session-id">
            Session: {sessionId.slice(0, 20)}...
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
